/**
 * Universal lightweight slider controller for Dawn.
 *
 * Required markup:
 *   data-custom-slider
 *   data-slider-list
 *   data-slider-slide
 * Optional:
 *   data-slider-prev
 *   data-slider-next
 *   data-slider-dots
 *
 * Supported data attributes:
 *   data-desktop-enabled
 *   data-mobile-enabled
 *   data-desktop-per-move
 *   data-mobile-per-move
 *   data-autoplay
 *   data-autoplay-speed
 *   data-pause-on-hover
 *   data-loop
 */
(() => {
  const ROOT_SELECTOR = '[data-custom-slider]';
  const initialized = new WeakSet();

  class CustomSlider {
    constructor(root) {
      this.root = root;
      this.list = root.querySelector('[data-slider-list]');
      this.slides = Array.from(root.querySelectorAll('[data-slider-slide]'));
      this.prevButton = root.querySelector('[data-slider-prev]');
      this.nextButton = root.querySelector('[data-slider-next]');
      this.dotsContainer = root.querySelector('[data-slider-dots]');

      this.desktopEnabled = root.dataset.desktopEnabled !== 'false';
      this.mobileEnabled = root.dataset.mobileEnabled !== 'false';
      this.desktopPerMove = Math.max(1, parseInt(root.dataset.desktopPerMove || '1', 10));
      this.mobilePerMove = Math.max(1, parseInt(root.dataset.mobilePerMove || '1', 10));

      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.autoplaySpeed = Math.max(2000, parseInt(root.dataset.autoplaySpeed || '5000', 10));
      this.pauseOnHover = root.dataset.pauseOnHover === 'true';
      this.loop = root.dataset.loop === 'true';

      this.positions = [];
      this.activeIndex = 0;
      this.timer = null;
      this.resizeTimer = null;
      this.scrollFrame = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      if (!this.list || !this.slides.length) return;
      this.init();
    }

    isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    isEnabled() {
      return this.isMobile() ? this.mobileEnabled : this.desktopEnabled;
    }

    getPerMove() {
      return this.isMobile() ? this.mobilePerMove : this.desktopPerMove;
    }

    getVisibleCount() {
      const listRect = this.list.getBoundingClientRect();
      if (!listRect.width) return 1;

      let count = 0;

      for (const slide of this.slides) {
        const rect = slide.getBoundingClientRect();
        if (rect.left < listRect.right - 2 && rect.right > listRect.left + 2) {
          count += 1;
        }
      }

      return Math.max(1, count);
    }

    buildPositions() {
      if (!this.isEnabled()) {
        this.positions = [];
        return;
      }

      const visibleCount = this.getVisibleCount();
      const perMove = this.getPerMove();
      const lastStart = Math.max(0, this.slides.length - visibleCount);
      const indexes = [];

      for (let index = 0; index <= lastStart; index += perMove) {
        indexes.push(index);
      }

      if (!indexes.length) indexes.push(0);

      if (indexes[indexes.length - 1] !== lastStart) {
        indexes.push(lastStart);
      }

      this.positions = indexes.map((index) => this.slides[index].offsetLeft);
    }

    buildDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.innerHTML = '';

      this.positions.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = this.root.dataset.dotClass || 'custom-slider-dot';
        dot.setAttribute('aria-label', `Go to slider page ${index + 1}`);
        dot.setAttribute('aria-current', index === this.activeIndex ? 'true' : 'false');
        dot.addEventListener('click', () => this.goTo(index));
        this.dotsContainer.appendChild(dot);
      });

      this.dotsContainer.hidden = this.positions.length <= 1;
    }

    goTo(index) {
      if (!this.positions.length) return;

      let target = index;

      if (target < 0) {
        target = this.loop ? this.positions.length - 1 : 0;
      }

      if (target >= this.positions.length) {
        target = this.loop ? 0 : this.positions.length - 1;
      }

      this.list.scrollTo({
        left: this.positions[target],
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });

      this.activeIndex = target;
      this.updateControls();
      this.restartAutoplay();
    }

    updateActiveFromScroll() {
      if (!this.positions.length) return;

      const current = this.list.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      this.positions.forEach((position, index) => {
        const distance = Math.abs(current - position);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      this.activeIndex = nearestIndex;
      this.updateControls();
      this.handleVideos();
    }

    updateControls() {
      this.dotsContainer?.querySelectorAll('[aria-current]').forEach((dot, index) => {
        dot.setAttribute('aria-current', index === this.activeIndex ? 'true' : 'false');
      });

      if (this.loop) {
        if (this.prevButton) this.prevButton.disabled = false;
        if (this.nextButton) this.nextButton.disabled = false;
        return;
      }

      if (this.prevButton) this.prevButton.disabled = this.activeIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex >= this.positions.length - 1;
    }

    handleVideos() {
      const listRect = this.list.getBoundingClientRect();

      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;

        const rect = slide.getBoundingClientRect();
        const visible = rect.left < listRect.right && rect.right > listRect.left;

        if (!visible && !video.paused) {
          video.pause();
        } else if (visible && video.autoplay && video.muted) {
          video.play().catch(() => {});
        }
      });
    }

    bindVideoControls() {
      this.root.querySelectorAll('[data-video-toggle]').forEach((button) => {
        const wrapper = button.closest('.diy-slider__video-wrap');
        const video = wrapper?.querySelector('video');
        if (!video || button.dataset.videoBound === 'true') return;

        button.dataset.videoBound = 'true';

        const syncButton = () => {
          const isPlaying = !video.paused && !video.ended;
          button.classList.toggle('is-playing', isPlaying);
          button.classList.toggle('is-hidden', isPlaying);
          button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
        };

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (video.paused || video.ended) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });

        video.addEventListener('play', syncButton);
        video.addEventListener('pause', syncButton);
        video.addEventListener('ended', syncButton);

        video.addEventListener('click', (event) => {
          if (video.controls) return;
          event.preventDefault();

          if (video.paused || video.ended) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });

        syncButton();
      });
    }

    refresh() {
      this.buildPositions();
      this.activeIndex = Math.min(this.activeIndex, Math.max(0, this.positions.length - 1));
      this.buildDots();
      this.updateControls();
      this.handleVideos();
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        !this.isEnabled() ||
        this.positions.length <= 1 ||
        document.hidden ||
        this.timer
      ) {
        return;
      }

      this.timer = window.setInterval(() => {
        this.goTo(this.activeIndex + 1);
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      if (!this.autoplayEnabled) return;
      this.stopAutoplay();
      this.startAutoplay();
    }

    init() {
      this.bindVideoControls();
      this.refresh();

      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.prevButton?.addEventListener('click', (event) => {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
      });

      this.nextButton?.addEventListener('click', (event) => {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
      });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', () => this.stopAutoplay());
        this.root.addEventListener('mouseleave', () => this.startAutoplay());
      }

      this.root.addEventListener('focusin', () => this.stopAutoplay());
      this.root.addEventListener('focusout', () => this.startAutoplay());

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });

      this.startAutoplay();
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateActiveFromScroll());
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.refresh();
        this.restartAutoplay();
      }, 160);
    }
  }

  function initCustomSliders(root = document) {
    root.querySelectorAll(ROOT_SELECTOR).forEach((slider) => {
      if (initialized.has(slider)) return;
      initialized.add(slider);
      new CustomSlider(slider);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initCustomSliders());

  document.addEventListener('shopify:section:load', (event) => {
    initCustomSliders(event.target);
  });
})();